<script lang="ts">
    import type {Row} from '@tanstack/svelte-table';
    import type {HierarchicalRecordRow, RecordRow} from 'src/features/record-grid/lib/types';
    import type {ReportTemplate} from 'typings/portaro.be.types';
    import type {RecordGridDataManager} from 'src/features/record-grid/lib/record-grid-data-manager';
    import {getProjectLogbookPairingContext} from './project-logbook-pairing-context';
    import {getInjector} from 'core/svelte-context/context';
    import {onMount} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import {getFirstFieldByFieldTypeIdRecursive} from 'src/features/record-grid/lib/grid-fields';
    import {getTextFromGridFieldValue} from 'src/features/erp-sutor/sutor-utils';
    import ReportServerService from 'src/features/report-server/services/report-server.service';
    import {FOND_INSTALLATION_LOGBOOK} from 'src/features/erp-sutor/sutor-fonds';
    import {SUTOR_LOGBOOK_PAIRING_PAGE} from 'src/features/erp-sutor/sutor-constants';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';
    import Spacer from 'shared/layouts/flex/Spacer.svelte';
    import DefaultRowOptionsCell from 'src/features/record-grid/components/cells/DefaultRowOptionsCell.svelte';
    import KpDropdownMenuButton from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuButton.svelte';
    import KpDropdownMenuItem from 'shared/ui-widgets/dropdown-menu/KpDropdownMenuItem.svelte';

    export let recordGridDataManager: RecordGridDataManager;
    export let row: Row<HierarchicalRecordRow>;

    const context = getProjectLogbookPairingContext();
    const reportServerService = getInjector().getByClass(ReportServerService);

    const handleOptimiseLogbookClick = () => {
        context.setOpenedLogbook(row.original);
    };

    let loadedTemplate: ReportTemplate | null = null;

    onMount(async () => {
        loadedTemplate = await context.service.getReportServerTemplateByName(SUTOR_LOGBOOK_PAIRING_PAGE.reportServerTemplateMD);
    });

    const handleDownloadClick = async () => {
        await reportServerService.downloadExport(loadedTemplate, {recordId: row.original.id}, getLogbookExportFileName(row.original));
    };

    const handleDisplayClick = async () => {
        const blob = await reportServerService.getBlobExport(loadedTemplate, {recordId: row.original.id}, getLogbookExportFileName(row.original));

        if (!exists(blob)) {
            return;
        }

        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
    };

    const handleSaveToFilesClick = async () => {
        const blob = await reportServerService.getBlobExport(loadedTemplate, {recordId: row.original.id}, getLogbookExportFileName(row.original));

        if (!exists(blob)) {
            return;
        }

        await context.service.saveLogbookExportToProjectFiles(context.projectRecord, blob);
    };

    function getLogbookExportFileName(logbookRecord: RecordRow): string {
        const parentItemField = getTextFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.parentItemFieldId), null);
        const customerProjectField = getTextFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.customerProjectFieldId), null);
        const dateField = getTextFromGridFieldValue(getFirstFieldByFieldTypeIdRecursive(logbookRecord, FOND_INSTALLATION_LOGBOOK.dateFieldId), null);
        const name = logbookRecord.name;

        const joinedName = [parentItemField, customerProjectField, dateField, name].filter((v) => exists(v)).join('-');
        return `${joinedName}.pdf`;
    }
</script>

<DefaultRowOptionsCell {recordGridDataManager} {row}>
    <Spacer flex="1"/>

    {#if FOND_INSTALLATION_LOGBOOK.fond === row.original.fond.id && !row.original.draft}
        {#if exists(loadedTemplate)}
            <KpDropdownMenuButton buttonSize="xs" tooltipLabel="Export">
                <svelte:fragment slot="button">
                    <IconedContent icon="print" trailingIcon="angle-small-down"/>
                </svelte:fragment>

                <svelte:fragment slot="menu">
                    <KpDropdownMenuItem icon="eye" on:click={handleDisplayClick}>Zobrazit</KpDropdownMenuItem>
                    <KpDropdownMenuItem icon="download" on:click={handleDownloadClick}>Stáhnout soubor</KpDropdownMenuItem>
                    <KpDropdownMenuItem icon="file-export" on:click={handleSaveToFilesClick}>Export k souborům zakázky</KpDropdownMenuItem>
                </svelte:fragment>
            </KpDropdownMenuButton>
        {/if}

        <KpButton buttonSize="xs"
                  tooltipLabel="Přivázat náklady k montážnímu deníku"
                  buttonStyle="brand-orange-new"
                  on:click={handleOptimiseLogbookClick}>

            <IconedContent icon="link-horizontal"/>
        </KpButton>
    {/if}
</DefaultRowOptionsCell>